import React, { useRef, useState, useEffect } from "react";
import { Drawer, Box, IconButton, Tooltip, Divider, Button, TextField, MenuItem, Menu } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import PreviewIcon from "@mui/icons-material/Preview";
import EditIcon from "@mui/icons-material/Edit";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import LinkIcon from "@mui/icons-material/Link";
import ImageIcon from "@mui/icons-material/Image";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import FormatSizeIcon from "@mui/icons-material/FormatSize";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import TableChartIcon from "@mui/icons-material/TableChart";
import { ColorPicker as ColorPickerPopover } from "@/components/Importable/Pickers";
import { getContrastForBackground } from "@/theme/colors";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export default function EditorToolbarDrawer(props) {
  const {
    open = true,
    // Wider default lets the inline toolbar fit 5 size="large" IconButtons
    // per row (each ~48px) plus their gaps without wrapping awkwardly.
    width = 320,
    variant = "permanent",
    previewMode,
    onAction,
    onTogglePreview,
    onClose,
    bottomExtras,
    activeActions,
    canUndo,
    canRedo,
    accentColor,
    onRequestLink,
    allowedControls,
    allowedInline,
    assets,
    onUploadAssets,
    onInsertImageContainer,
    onInsertImageWithText,
    onInsertAssetsAsRow,
    onUpdateAssetSize,
    onRemoveAsset,
    onInsertSelectedAsRow,
    hideAssetsControls,
    onInsertTable,
    textColorValue = null,
    highlightValue = null,
    onColorChange,
    isSelectionInTable = false,
    isSelectionInTableHeader = false
  } = props;
  const inlineButtons = allowedInline && allowedInline.length ? allowedInline : ["bold", "italic", "underline", "strike"];
  const [selected, setSelected] = useState({});

  // Color picker anchors + open state
  const textColorBtnRef = useRef(null);
  const highlightBtnRef = useRef(null);

  // The presets section is the only scrollable area. When it overflows
  // it grows a vertical scrollbar that occupies a few pixels of its own
  // width — that's intentional, the presets don't need the full toolbar
  // width and the surrounding pinned sections stay at their normal size.
  const scrollRef = useRef(null);
  const [textColorPickerOpen, setTextColorPickerOpen] = useState(false);
  const [highlightPickerOpen, setHighlightPickerOpen] = useState(false);
  const [fontSizeMenuAnchorEl, setFontSizeMenuAnchorEl] = useState(null);
  const [fontSizeValue, setFontSizeValue] = useState("standard");
  const fontSizeMenuOpen = Boolean(fontSizeMenuAnchorEl);

  // Platform detection (runs client-side only to avoid SSR mismatch)
  const [isMac, setIsMac] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    setIsMac(/Mac|iPhone|iPod|iPad/.test(navigator.platform));
    setIsMobile(/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent));
  }, []);
  const mod = isMac ? "⌘" : "Ctrl";
  // Returns a tooltip title with optional shortcut appended, or empty on mobile.
  const tip = (label, shortcut) => isMobile ? "" : shortcut ? `${label} (${shortcut})` : label;

  // ── Live selection tracking ───────────────────────────────────────────
  // Tracks whether the user currently has a non-collapsed text selection
  // somewhere in the document. Used to swap tooltip text on buttons that
  // require an active selection to do anything useful.
  const [hasSelection, setHasSelection] = useState(false);
  useEffect(() => {
    const update = () => {
      const sel = window.getSelection();
      setHasSelection(Boolean(sel && !sel.isCollapsed && sel.toString().length > 0));
    };
    document.addEventListener("selectionchange", update);
    update();
    return () => document.removeEventListener("selectionchange", update);
  }, []);
  // Tooltip text for buttons that require a selection. Falls back to the
  // "please highlight" hint when there is no active selection so users
  // understand why the button has no effect.
  const NEED_SELECTION_TIP = "Please highlight a selection of text with your cursor to proceed";
  const selTip = (label, shortcut) => hasSelection ? tip(label, shortcut) : NEED_SELECTION_TIP;
  const SHORTCUTS = {
    bold: `${mod}+B`,
    italic: `${mod}+I`,
    underline: `${mod}+U`,
    undo: `${mod}+Z`,
    redo: `${mod}+Shift+Z`
  };
  // Recently-used color slots no longer carry their own keyboard
  // shortcuts — the modifier+digit chord is now reserved for style
  // presets (see preset shortcut handler below). Click applies as text
  // color; Shift+click applies as highlight.
  const RECENT_TEXT_CLICK_HINT = "Click: text color · Shift+Click: highlight";
  const ALT_MOD = isMac ? "⌥+⌘" : "Alt+Ctrl";
  const FONT_SIZE_SHORTCUT = `${ALT_MOD}+= / ${ALT_MOD}+-`;
  const INSERT_LINK_SHORTCUT = `${ALT_MOD}+:`;
  const LABELS = {
    bold: "Bold",
    italic: "Italic",
    underline: "Underline",
    strike: "Strikethrough"
  };

  // Recently used colors – fixed 10 slots mapped to keyboard keys 1–9
  // then 0 (matching keyboard layout). `null` means the slot is empty
  // and rendered as a dashed placeholder the user can click to assign a
  // color via the picker.
  const HOTKEY_SLOT_COUNT = 10;
  const [recentColors, setRecentColors] = useState(() => Array.from({
    length: HOTKEY_SLOT_COUNT
  }, () => null));
  const addRecentColor = color => {
    if (!color) return;
    setRecentColors(prev => {
      // Already bound to a hotkey – nothing to do.
      if (prev.some(c => c === color)) return prev;
      // Find the first empty slot. If every slot is filled we leave
      // the existing bindings alone; the user must explicitly free a
      // slot via the hotkey editor before a new one is recorded.
      const emptyIdx = prev.findIndex(c => c === null);
      if (emptyIdx === -1) return prev;
      const next = [...prev];
      next[emptyIdx] = color;
      return next;
    });
  };
  // Edit mode toggles the pencil affordance next to the recent-colors
  // label. While active, clicking a slot opens the color picker so the
  // user can replace or delete the bound hotkey.
  const [editingHotkeys, setEditingHotkeys] = useState(false);
  const [hotkeyEditAnchor, setHotkeyEditAnchor] = useState(null);
  // Determines whether a hex color is perceptually light (for text contrast).
  const isLightColor = hex => {
    const m = hex.replace("#", "").match(/.{2}/g);
    if (!m || m.length < 3) return true;
    const [r, g, b] = m.map(x => parseInt(x, 16));
    return 0.299 * r + 0.587 * g + 0.114 * b > 140;
  };
  // Pick the icon foreground for a swatch chip. Prefer the palette's
  // explicit contrast pairing (e.g. yellow primary → white) so the
  // designer-curated text color always wins; fall back to a luminance
  // heuristic for arbitrary user-chosen colors.
  const chipIconColor = bg => {
    if (!bg) return undefined;
    const palette = getContrastForBackground(bg);
    if (palette) return palette;
    return isLightColor(bg) ? "#000" : "#fff";
  };

  // ── Style presets ────────────────────────────────────────────────────
  // A preset captures the current selection's bold/italic/underline/strike
  // state plus optional text color, highlight color, and font-size bucket.
  // The user can later click "Apply" on a preset to re-apply that exact
  // combination to a different selection.

  // Two default presets the user can edit or delete freely. Colors are
  // sourced from the shared palette: blue primary (#2196f3) and the light
  // yellow swatch (#ffeeb2) so they round-trip through the palette
  // contrast map.
  const defaultPresets = [{
    id: "preset_default_section_title",
    name: "Section Title",
    bold: true,
    italic: true,
    underline: true,
    textColor: "#2196f3",
    fontSize: "extraLarge"
  }, {
    id: "preset_default_important_date",
    name: "Important Date",
    italic: true,
    underline: true,
    highlight: "#ffeeb2"
  }];
  const [formatPresets, setFormatPresets] = useState(defaultPresets);
  const [presetCounter, setPresetCounter] = useState(0);
  // Presets currently in edit mode. Multiple may be edited at once.
  const [editingIds, setEditingIds] = useState(new Set());
  const isEditing = id => editingIds.has(id);
  const startEditing = id => setEditingIds(prev => {
    const n = new Set(prev);
    n.add(id);
    return n;
  });
  const stopEditing = id => setEditingIds(prev => {
    const n = new Set(prev);
    n.delete(id);
    return n;
  });
  // Two-step delete confirmation while editing.
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  // Anchor for the per-preset color picker popover (text color or highlight).
  const [presetColorPicker, setPresetColorPicker] = useState(null);
  const handleCreateBlankPreset = () => {
    const next = presetCounter + 1;
    const preset = {
      id: "preset_" + Math.random().toString(36).slice(2, 9),
      name: `Preset ${next}`
    };
    setPresetCounter(next);
    setFormatPresets(prev => [...prev, preset]);
    startEditing(preset.id);
  };
  const updatePreset = (id, patch) => {
    setFormatPresets(prev => prev.map(p => {
      if (p.id !== id) return p;
      const update = typeof patch === "function" ? patch(p) : patch;
      return {
        ...p,
        ...update
      };
    }));
  };
  const togglePresetBool = (id, key) => {
    updatePreset(id, p => ({
      [key]: !p[key] || undefined
    }));
  };
  const renamePreset = (id, name) => {
    setFormatPresets(prev => prev.map(p => p.id === id ? {
      ...p,
      name
    } : p));
  };
  const deletePreset = id => {
    setFormatPresets(prev => prev.filter(p => p.id !== id));
  };
  const applyPreset = preset => {
    // Force-apply the preset as a single atomic DOM operation in the
    // editor hook. The old approach chained toggling onAction calls
    // (bold, italic, …) which read the surrounding element's
    // *computed* style to decide whether to add or remove the format —
    // for selections sitting inside paragraphs whose ancestors carry
    // inherited weight/size, the toggle would incorrectly flip OFF
    // and produce inconsistent results across different selections
    // (e.g. it worked on the first sentence but not on short labels
    // like "Part 15"). The "applyPreset" action handles the entire
    // wrap + reselect + state-sync atomically so every selection
    // ends up with the same exact wrappers.
    onAction("applyPreset", {
      bold: preset.bold,
      italic: preset.italic,
      underline: preset.underline,
      strike: preset.strike,
      textColor: preset.textColor,
      highlight: preset.highlight,
      fontSize: preset.fontSize
    });
  };

  // Global keyboard shortcuts (applied while text is selected in the page).
  useEffect(() => {
    const hasSelection = () => {
      const sel = window.getSelection();
      return Boolean(sel && !sel.isCollapsed);
    };
    const handler = e => {
      const modKey = isMac ? e.metaKey : e.ctrlKey;
      // ── Style preset shortcuts ───────────────────────────────────────
      // Mod+1..9 → presets[0..8], Mod+0 → presets[9]. Shift is
      // ignored so the chord doesn't collide with native browser
      // shortcuts that overload Shift+digit. Requires an active
      // selection — applyPreset's removeFormat baseline only runs
      // when text is highlighted.
      if (modKey && !e.altKey && !e.shiftKey) {
        let presetIndex = null;
        if (e.key >= "1" && e.key <= "9") {
          presetIndex = parseInt(e.key, 10) - 1;
        } else if (e.key === "0") {
          presetIndex = 9;
        }
        if (presetIndex !== null) {
          const preset = formatPresets[presetIndex];
          if (preset && hasSelection()) {
            e.preventDefault();
            applyPreset(preset);
            return;
          }
        }
      }
      // ── Font size shortcuts ──────────────────────────────────────────
      // Determine the current selection's rendered font-size and step
      // it up/down through the same buckets the dropdown uses. This
      // ensures shortcuts behave like clicking the corresponding
      // dropdown option for whatever is currently selected, instead of
      // tracking their own (potentially stale) value.
      const sizeOrder = ["standard", "large", "extraLarge"];
      const currentSelectionPx = () => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return null;
        const range = sel.getRangeAt(0);
        let node = range.startContainer;
        if (node && node.nodeType === Node.TEXT_NODE) node = node.parentNode;
        if (!(node instanceof HTMLElement)) return null;
        const px = parseFloat(window.getComputedStyle(node).fontSize);
        return isNaN(px) ? null : px;
      };
      const bucketFromPx = px => {
        if (px == null) return "standard";
        // 24px ~ large, 32px ~ extra large; pick by nearest band.
        if (px >= 28) return "extraLarge";
        if (px >= 20) return "large";
        return "standard";
      };
      const stepFontSize = direction => {
        const current = bucketFromPx(currentSelectionPx());
        const idx = sizeOrder.indexOf(current);
        const nextIdx = Math.min(sizeOrder.length - 1, Math.max(0, idx + direction));
        const next = sizeOrder[nextIdx];
        if (next === current) return;
        setFontSizeValue(next);
        onAction("fontSize", next);
      };
      // Option+Mod+Equal key → size up. Use `code` so macOS Option
      // character transforms (e.g. "≠") do not break detection.
      if (e.altKey && modKey && e.code === "Equal" && hasSelection()) {
        e.preventDefault();
        stepFontSize(1);
        return;
      }
      // Option+Mod+Minus key → size down
      if (e.altKey && modKey && e.code === "Minus" && hasSelection()) {
        e.preventDefault();
        stepFontSize(-1);
        return;
      }
      // ── Link shortcut ────────────────────────────────────────────────
      // Option+Mod+: → insert link
      if (e.altKey && modKey && e.key === ":" && hasSelection()) {
        e.preventDefault();
        onRequestLink?.();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [formatPresets, isMac, fontSizeValue, onAction, onColorChange, onRequestLink]);
  const toggleSelect = id => setSelected(s => ({
    ...s,
    [id]: !s[id]
  }));
  const selectedIds = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
  const selectableSelectedIds = (assets || []).filter(a => selected[a.id] && !a.inserted).map(a => a.id);
  return /*#__PURE__*/_jsxs(Drawer, {
    anchor: "left",
    variant: variant,
    open: open,
    onClose: onClose,
    sx: {
      zIndex: 1201
    },
    PaperProps: {
      "data-select-target": "Toolbar",
      "data-pc-no-promote-z": "1",
      sx: {
        width,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden"
      }
    },
    children: [!hideAssetsControls && (onInsertImageContainer || onInsertImageWithText || onInsertTable) && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Box, {
        sx: {
          px: 2,
          py: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 1
        },
        onMouseDown: e => {
          const target = e.target;
          if (target.closest("input, textarea, select, [contenteditable='true']")) {
            return;
          }
          e.preventDefault();
        },
        children: [onInsertImageContainer && /*#__PURE__*/_jsx(Button, {
          variant: "contained",
          size: "small",
          onClick: () => onInsertImageContainer(),
          startIcon: /*#__PURE__*/_jsx(ViewCarouselIcon, {}),
          fullWidth: true,
          sx: {
            minHeight: 46
          },
          children: "Insert Image Row"
        }), onInsertImageWithText && /*#__PURE__*/_jsx(Button, {
          variant: "outlined",
          size: "small",
          onClick: () => onInsertImageWithText(),
          startIcon: /*#__PURE__*/_jsx(ImageIcon, {}),
          fullWidth: true,
          sx: {
            minHeight: 46
          },
          children: "Insert Image with Text"
        }), onInsertTable && /*#__PURE__*/_jsx(Button, {
          variant: "outlined",
          size: "small",
          onClick: () => onInsertTable(),
          startIcon: /*#__PURE__*/_jsx(TableChartIcon, {}),
          fullWidth: true,
          sx: {
            minHeight: 46
          },
          children: "Insert Table"
        })]
      }), /*#__PURE__*/_jsx(Divider, {})]
    }), /*#__PURE__*/_jsxs(Box, {
      sx: {
        p: 2
      }
      // Prevent the editor's text selection from being cleared when
      // the user clicks anywhere inside the toolbar. Buttons inside
      // already preventDefault on their own, but the surrounding Box
      // / dividers / labels would otherwise steal focus and collapse
      // the selection. Using mousedown (not click) so it runs before
      // the browser commits the selection change.
      ,
      onMouseDown: e => {
        // Allow form fields (e.g. the URL input inside link
        // creation popovers) to still receive focus.
        const target = e.target;
        if (target.closest("input, textarea, select, [contenteditable='true']")) {
          return;
        }
        e.preventDefault();
      },
      children: [/*#__PURE__*/_jsxs(Box, {
        sx: {
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          rowGap: 1,
          mb: 1
        },
        children: [(allowedControls?.formatting ?? true) && inlineButtons.map(key => {
          const active = activeActions?.has(key);
          return /*#__PURE__*/_jsx(Tooltip, {
            title: selTip(LABELS[key] ?? key, SHORTCUTS[key]),
            placement: "top",
            arrow: true,
            children: /*#__PURE__*/_jsx(IconButton, {
              size: "large",
              onClick: () => onAction(key),
              "aria-label": key,
              color: active ? "primary" : "default",
              sx: active ? {
                border: "1px solid",
                borderColor: accentColor || "primary.main",
                color: accentColor || undefined
              } : undefined,
              children: key === "bold" ? /*#__PURE__*/_jsx(FormatBoldIcon, {
                fontSize: "medium"
              }) : key === "underline" ? /*#__PURE__*/_jsx(FormatUnderlinedIcon, {
                fontSize: "medium"
              }) : key === "strike" ? /*#__PURE__*/_jsx(StrikethroughSIcon, {
                fontSize: "medium"
              }) : /*#__PURE__*/_jsx(FormatItalicIcon, {
                fontSize: "medium"
              })
            })
          }, key);
        }), (allowedControls?.formatting ?? true) && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(Tooltip, {
            title: isSelectionInTable ? "Font size is not supported for table cells" : selTip("Font size", FONT_SIZE_SHORTCUT),
            placement: "top",
            arrow: true,
            children: /*#__PURE__*/_jsx("span", {
              children: /*#__PURE__*/_jsx(IconButton, {
                size: "large",
                "aria-label": "Font size",
                disabled: isSelectionInTable,
                onMouseDown: e => e.preventDefault(),
                onClick: e => setFontSizeMenuAnchorEl(e.currentTarget),
                children: /*#__PURE__*/_jsx(FormatSizeIcon, {
                  fontSize: "medium"
                })
              })
            })
          }), /*#__PURE__*/_jsxs(Menu, {
            anchorEl: fontSizeMenuAnchorEl,
            open: fontSizeMenuOpen,
            onClose: () => setFontSizeMenuAnchorEl(null),
            onMouseDown: e => e.preventDefault(),
            MenuListProps: {
              "aria-label": "Font size options"
            },
            disableAutoFocus: true,
            disableEnforceFocus: true,
            disableRestoreFocus: true,
            children: [/*#__PURE__*/_jsx(MenuItem, {
              selected: fontSizeValue === "standard",
              onClick: () => {
                setFontSizeValue("standard");
                onAction("fontSize", "standard");
                setFontSizeMenuAnchorEl(null);
              },
              children: "Standard"
            }), /*#__PURE__*/_jsx(MenuItem, {
              selected: fontSizeValue === "large",
              onClick: () => {
                setFontSizeValue("large");
                onAction("fontSize", "large");
                setFontSizeMenuAnchorEl(null);
              },
              children: "Large"
            }), /*#__PURE__*/_jsx(MenuItem, {
              selected: fontSizeValue === "extraLarge",
              onClick: () => {
                setFontSizeValue("extraLarge");
                onAction("fontSize", "extraLarge");
                setFontSizeMenuAnchorEl(null);
              },
              children: "Extra Large"
            })]
          }), /*#__PURE__*/_jsx(Tooltip, {
            title: isSelectionInTableHeader ? "Text color is not supported for table header cells" : selTip("Text Color"),
            placement: "top",
            arrow: true,
            children: /*#__PURE__*/_jsx("span", {
              children: /*#__PURE__*/_jsx(IconButton, {
                ref: textColorBtnRef,
                size: "large",
                "aria-label": "Text Color",
                disabled: isSelectionInTableHeader,
                onMouseDown: e => e.preventDefault(),
                onClick: () => setTextColorPickerOpen(o => !o),
                children: /*#__PURE__*/_jsx(FormatColorTextIcon, {
                  fontSize: "medium"
                })
              })
            })
          }), /*#__PURE__*/_jsx(Tooltip, {
            title: isSelectionInTableHeader ? "Highlight is not supported for table header cells" : selTip("Highlight Color"),
            placement: "top",
            arrow: true,
            children: /*#__PURE__*/_jsx("span", {
              children: /*#__PURE__*/_jsx(IconButton, {
                ref: highlightBtnRef,
                size: "large",
                "aria-label": "Highlight Color",
                disabled: isSelectionInTableHeader,
                onMouseDown: e => e.preventDefault(),
                onClick: () => setHighlightPickerOpen(o => !o),
                children: /*#__PURE__*/_jsx(BorderColorIcon, {
                  fontSize: "medium"
                })
              })
            })
          }), /*#__PURE__*/_jsx(ColorPickerPopover, {
            anchorEl: textColorBtnRef.current,
            open: textColorPickerOpen,
            onClose: () => setTextColorPickerOpen(false),
            currentColor: textColorValue,
            title: "Text Color",
            removeLabel: "Default text color",
            onSelect: color => {
              addRecentColor(color);
              onColorChange?.("textColor", color);
              onAction("textColor", color);
            }
          }), /*#__PURE__*/_jsx(ColorPickerPopover, {
            anchorEl: highlightBtnRef.current,
            open: highlightPickerOpen,
            onClose: () => setHighlightPickerOpen(false),
            currentColor: highlightValue,
            title: "Highlight Color",
            removeLabel: "Remove highlight",
            onSelect: color => {
              addRecentColor(color);
              onColorChange?.("highlight", color);
              onAction("highlight", color);
            }
          })]
        }), (allowedControls?.link ?? true) && /*#__PURE__*/_jsx(_Fragment, {
          children: /*#__PURE__*/_jsx(Tooltip, {
            title: selTip("Insert Link", INSERT_LINK_SHORTCUT),
            placement: "top",
            arrow: true,
            children: /*#__PURE__*/_jsx(IconButton, {
              size: "large",
              onClick: () => onRequestLink && onRequestLink(),
              "aria-label": "Insert Link",
              children: /*#__PURE__*/_jsx(LinkIcon, {
                fontSize: "medium"
              })
            })
          })
        }), (allowedControls?.undoRedo ?? true) && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(Tooltip, {
            title: tip("Undo", SHORTCUTS.undo),
            placement: "top",
            arrow: true,
            children: /*#__PURE__*/_jsx("span", {
              children: /*#__PURE__*/_jsx(IconButton, {
                size: "large",
                onClick: () => canUndo && onAction("undo"),
                "aria-label": "Undo",
                disabled: !canUndo,
                children: /*#__PURE__*/_jsx(UndoIcon, {
                  fontSize: "medium"
                })
              })
            })
          }), /*#__PURE__*/_jsx(Tooltip, {
            title: tip("Redo", SHORTCUTS.redo),
            placement: "top",
            arrow: true,
            children: /*#__PURE__*/_jsx("span", {
              children: /*#__PURE__*/_jsx(IconButton, {
                size: "large",
                onClick: () => canRedo && onAction("redo"),
                "aria-label": "Redo",
                disabled: !canRedo,
                children: /*#__PURE__*/_jsx(RedoIcon, {
                  fontSize: "medium"
                })
              })
            })
          })]
        })]
      }), (allowedControls?.clear ?? true) && /*#__PURE__*/_jsx(Tooltip, {
        title: selTip("Clear Formatting"),
        placement: "top",
        arrow: true,
        children: /*#__PURE__*/_jsx("span", {
          style: {
            display: "block"
          },
          children: /*#__PURE__*/_jsx(Button, {
            variant: "outlined",
            size: "small",
            disabled: !hasSelection,
            onMouseDown: e => e.preventDefault(),
            onClick: () => {
              // Reset the toolbar's font-size dropdown
              // so the user can immediately pick
              // "extraLarge" again after clearing
              // instead of bouncing through "standard".
              setFontSizeValue("standard");
              onAction("clear");
            },
            fullWidth: true,
            sx: {
              mt: 1,
              ...(accentColor && {
                borderColor: accentColor,
                color: accentColor
              })
            },
            children: "Clear Formatting"
          })
        })
      })]
    }), /*#__PURE__*/_jsx(Divider, {}), /*#__PURE__*/_jsxs(Box, {
      sx: {
        px: 2,
        py: 1.5
      },
      children: [/*#__PURE__*/_jsxs(Box, {
        sx: {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1
        },
        children: [/*#__PURE__*/_jsx(Box, {
          sx: {
            fontSize: 14,
            color: "text.primary",
            fontWeight: 600
          },
          children: "Recently used colors"
        }), /*#__PURE__*/_jsx(Tooltip, {
          title: editingHotkeys ? "Done editing colors" : "Edit recent colors",
          placement: "top",
          arrow: true,
          children: /*#__PURE__*/_jsx(Button, {
            size: "small",
            onClick: () => {
              setEditingHotkeys(v => !v);
              setHotkeyEditAnchor(null);
            },
            "aria-label": editingHotkeys ? "Save color changes" : "Edit recent colors",
            color: editingHotkeys ? "primary" : "inherit",
            variant: editingHotkeys ? "contained" : "outlined",
            startIcon: editingHotkeys ? /*#__PURE__*/_jsx(CheckIcon, {
              sx: {
                fontSize: 16
              }
            }) : /*#__PURE__*/_jsx(EditIcon, {
              sx: {
                fontSize: 16
              }
            }),
            sx: {
              textTransform: "none",
              fontSize: 12,
              fontWeight: 600,
              py: 0.25,
              px: 1,
              minWidth: 0
            },
            children: editingHotkeys ? "Save changes" : "Edit"
          })
        })]
      }), /*#__PURE__*/_jsx(Box, {
        sx: {
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: 1
        },
        children: recentColors.map((color, i) => {
          const isEmpty = color === null;
          // Tooltip explains the new click behavior: plain
          // click applies the slot color as text color,
          // Shift+click applies it as a highlight. Empty
          // slots prompt the user to assign a color, and
          // edit mode swaps in management copy.
          const tooltipTitle = isEmpty ? "Assign a color" : editingHotkeys ? "Edit color" : selTip(`Apply color — ${RECENT_TEXT_CLICK_HINT}`);
          return /*#__PURE__*/_jsx(Tooltip, {
            title: tooltipTitle,
            placement: "top",
            arrow: true,
            children: /*#__PURE__*/_jsx(Box, {
              component: "span",
              onMouseDown: e => e.preventDefault(),
              onClick: e => {
                // Empty slots always open the picker
                // so the user can populate them.
                // Filled slots open the picker only
                // while edit mode is active.
                if (isEmpty || editingHotkeys) {
                  setHotkeyEditAnchor({
                    index: i,
                    anchor: e.currentTarget
                  });
                  return;
                }
                // Shift+click applies as highlight,
                // plain click applies as text color.
                if (e.shiftKey) {
                  onColorChange?.("highlight", color);
                  onAction("highlight", color);
                } else {
                  onColorChange?.("textColor", color);
                  onAction("textColor", color);
                }
              },
              sx: {
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                // Match toolbar IconButton (size large)
                // height/width so the hotkey grid feels
                // visually aligned with the buttons above.
                width: "100%",
                height: 44,
                bgcolor: isEmpty ? "transparent" : color,
                border: isEmpty || editingHotkeys ? "1px dashed" : "1px solid rgba(0,0,0,0.2)",
                borderColor: isEmpty || editingHotkeys ? isEmpty ? "divider" : "primary.main" : undefined,
                borderRadius: 0.5,
                cursor: "pointer",
                userSelect: "none",
                fontSize: 12,
                fontWeight: 700,
                color: isEmpty ? "text.disabled" : chipIconColor(color) || "#000",
                "&:hover": {
                  outline: "2px solid",
                  outlineColor: "primary.main"
                }
              },
              children: isEmpty ? "+" : null
            })
          }, i);
        })
      })]
    }), hotkeyEditAnchor && /*#__PURE__*/_jsx(ColorPickerPopover, {
      anchorEl: hotkeyEditAnchor.anchor,
      open: Boolean(hotkeyEditAnchor),
      onClose: () => setHotkeyEditAnchor(null),
      onSelect: c => {
        const idx = hotkeyEditAnchor.index;
        setRecentColors(prev => {
          const next = [...prev];
          if (c === null) {
            next[idx] = null;
            return next;
          }
          // If the chosen color already lives in another
          // slot, clear that other slot so each color
          // stays unique while preserving slot positions.
          const dupIdx = next.findIndex((existing, i) => i !== idx && existing === c);
          if (dupIdx !== -1) {
            next[dupIdx] = null;
          }
          next[idx] = c;
          return next;
        });
        setHotkeyEditAnchor(null);
      },
      currentColor: recentColors[hotkeyEditAnchor.index] ?? undefined,
      title: `Color hotkey ${hotkeyEditAnchor.index === 9 ? 0 : hotkeyEditAnchor.index + 1}`,
      hotkeyIndex: hotkeyEditAnchor.index
    }), /*#__PURE__*/_jsxs(Box, {
      ref: scrollRef,
      sx: {
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column"
      },
      children: [/*#__PURE__*/_jsx(Divider, {}), /*#__PURE__*/_jsxs(Box, {
        sx: {
          px: 2,
          py: 1.5
        },
        onMouseDown: e => {
          const target = e.target;
          if (target.closest("input, textarea, select, [contenteditable='true']")) {
            return;
          }
          e.preventDefault();
        },
        children: [/*#__PURE__*/_jsxs(Box, {
          sx: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: formatPresets.length > 0 ? 1 : 0
          },
          children: [/*#__PURE__*/_jsx(Box, {
            sx: {
              fontSize: 14,
              color: "text.primary",
              fontWeight: 600
            },
            children: "Style presets"
          }), /*#__PURE__*/_jsx(Tooltip, {
            title: "Create a new blank preset",
            placement: "top",
            arrow: true,
            children: /*#__PURE__*/_jsx(Button, {
              size: "small",
              onClick: handleCreateBlankPreset,
              "aria-label": "Create preset",
              color: "inherit",
              variant: "outlined",
              startIcon: /*#__PURE__*/_jsx(AddIcon, {
                sx: {
                  fontSize: 16
                }
              }),
              sx: {
                textTransform: "none",
                fontSize: 12,
                fontWeight: 600,
                ...(accentColor && {
                  borderColor: accentColor,
                  color: accentColor
                })
              },
              children: "Create"
            })
          })]
        }), formatPresets.length > 0 && /*#__PURE__*/_jsx(Box, {
          sx: {
            display: "flex",
            flexDirection: "column",
            gap: 1
          },
          children: formatPresets.map((preset, presetIndex) => {
            const editing = isEditing(preset.id);
            const confirming = confirmDeleteId === preset.id;
            // Presets[0..8] are bound to Mod+1..9, and
            // presets[9] is bound to Mod+0 (matching
            // keyboard layout). Anything beyond the 10th
            // preset has no shortcut.
            const presetShortcut = presetIndex < 9 ? `${mod}+${presetIndex + 1}` : presetIndex === 9 ? `${mod}+0` : undefined;
            // Reusable inline icon-style "chip" for both
            // view (read-only display) and edit (clickable
            // toggle) states.
            const chipSx = active => ({
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              border: "1px solid",
              borderColor: active ? "primary.main" : "divider",
              borderRadius: 0.5,
              cursor: editing ? "pointer" : "default",
              bgcolor: active ? "action.selected" : "transparent",
              color: active ? "primary.main" : "text.primary",
              "&:hover": editing ? {
                borderColor: "primary.main"
              } : undefined
            });
            const fontSizes = [{
              value: "standard",
              label: "S"
            }, {
              value: "large",
              label: "L"
            }, {
              value: "extraLarge",
              label: "XL"
            }];
            return /*#__PURE__*/_jsxs(Box, {
              sx: {
                position: "relative",
                border: "1px solid",
                borderColor: editing ? "primary.main" : "divider",
                borderRadius: 1,
                p: 1,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                // Reveal hover edit affordance
                "&:hover .preset-edit-affordance": {
                  opacity: 1
                }
              },
              children: [/*#__PURE__*/_jsxs(Box, {
                sx: {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1
                },
                children: [/*#__PURE__*/_jsx(TextField, {
                  size: "small",
                  variant: "standard",
                  value: preset.name,
                  onChange: e => renamePreset(preset.id, e.target.value),
                  disabled: !editing,
                  sx: {
                    flex: 1,
                    "& .MuiInputBase-input": {
                      fontSize: 13,
                      fontWeight: 500
                    },
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "rgba(0,0,0,0.87)"
                    }
                  }
                }), !editing && /*#__PURE__*/_jsx(Tooltip, {
                  title: "Edit preset",
                  placement: "left",
                  children: /*#__PURE__*/_jsx(IconButton, {
                    className: "preset-edit-affordance",
                    size: "small",
                    onClick: () => {
                      setConfirmDeleteId(null);
                      startEditing(preset.id);
                    },
                    "aria-label": "Edit preset",
                    sx: {
                      opacity: 0,
                      transition: "opacity 120ms",
                      width: 24,
                      height: 24,
                      "&:hover": {
                        borderColor: "primary.main"
                      }
                    },
                    children: /*#__PURE__*/_jsx(EditIcon, {
                      sx: {
                        fontSize: 16
                      }
                    })
                  })
                }), editing && /*#__PURE__*/_jsx(Tooltip, {
                  title: confirming ? "Click again to confirm delete" : "Delete preset",
                  children: /*#__PURE__*/_jsx(IconButton, {
                    size: "small",
                    onClick: () => {
                      if (confirming) {
                        deletePreset(preset.id);
                        stopEditing(preset.id);
                        setConfirmDeleteId(null);
                      } else {
                        setConfirmDeleteId(preset.id);
                      }
                    },
                    onBlur: () => confirming && setConfirmDeleteId(null),
                    "aria-label": "Delete preset",
                    color: confirming ? "error" : "default",
                    sx: confirming ? {
                      border: "1px solid",
                      borderColor: "error.main"
                    } : undefined,
                    children: /*#__PURE__*/_jsx(DeleteOutlineIcon, {
                      fontSize: "small"
                    })
                  })
                })]
              }), /*#__PURE__*/_jsxs(Box, {
                sx: {
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  alignItems: "center"
                },
                children: [(editing || preset.bold) && /*#__PURE__*/_jsx(Tooltip, {
                  title: "Bold",
                  children: /*#__PURE__*/_jsx(Box, {
                    component: "span",
                    onClick: () => editing && togglePresetBool(preset.id, "bold"),
                    sx: chipSx(!!preset.bold),
                    children: /*#__PURE__*/_jsx(FormatBoldIcon, {
                      sx: {
                        fontSize: 16
                      }
                    })
                  })
                }), (editing || preset.italic) && /*#__PURE__*/_jsx(Tooltip, {
                  title: "Italic",
                  children: /*#__PURE__*/_jsx(Box, {
                    component: "span",
                    onClick: () => editing && togglePresetBool(preset.id, "italic"),
                    sx: chipSx(!!preset.italic),
                    children: /*#__PURE__*/_jsx(FormatItalicIcon, {
                      sx: {
                        fontSize: 16
                      }
                    })
                  })
                }), (editing || preset.underline) && /*#__PURE__*/_jsx(Tooltip, {
                  title: "Underline",
                  children: /*#__PURE__*/_jsx(Box, {
                    component: "span",
                    onClick: () => editing && togglePresetBool(preset.id, "underline"),
                    sx: chipSx(!!preset.underline),
                    children: /*#__PURE__*/_jsx(FormatUnderlinedIcon, {
                      sx: {
                        fontSize: 16
                      }
                    })
                  })
                }), (editing || preset.strike) && /*#__PURE__*/_jsx(Tooltip, {
                  title: "Strikethrough",
                  children: /*#__PURE__*/_jsx(Box, {
                    component: "span",
                    onClick: () => editing && togglePresetBool(preset.id, "strike"),
                    sx: chipSx(!!preset.strike),
                    children: /*#__PURE__*/_jsx(StrikethroughSIcon, {
                      sx: {
                        fontSize: 16
                      }
                    })
                  })
                }), editing ? fontSizes.map(opt => /*#__PURE__*/_jsx(Tooltip, {
                  title: `Font size: ${opt.label}`,
                  children: /*#__PURE__*/_jsxs(Box, {
                    component: "span",
                    onClick: () => updatePreset(preset.id, {
                      fontSize: preset.fontSize === opt.value ? undefined : opt.value
                    }),
                    sx: {
                      ...chipSx(preset.fontSize === opt.value),
                      px: 0.75,
                      width: "auto",
                      gap: 0.25,
                      fontSize: 11,
                      fontWeight: 600
                    },
                    children: [/*#__PURE__*/_jsx(FormatSizeIcon, {
                      sx: {
                        fontSize: 14
                      }
                    }), opt.label]
                  })
                }, opt.value)) : preset.fontSize && /*#__PURE__*/_jsx(Tooltip, {
                  title: `Font size: ${preset.fontSize === "extraLarge" ? "Extra Large" : preset.fontSize === "large" ? "Large" : "Standard"}`,
                  children: /*#__PURE__*/_jsxs(Box, {
                    sx: {
                      ...chipSx(true),
                      px: 0.75,
                      width: "auto",
                      gap: 0.25,
                      fontSize: 11,
                      fontWeight: 600
                    },
                    children: [/*#__PURE__*/_jsx(FormatSizeIcon, {
                      sx: {
                        fontSize: 14
                      }
                    }), preset.fontSize === "extraLarge" ? "XL" : preset.fontSize === "large" ? "L" : "S"]
                  })
                }), (editing || preset.textColor) && /*#__PURE__*/_jsx(Tooltip, {
                  title: preset.textColor ? `Text color: ${preset.textColor}` : "Choose text color",
                  children: /*#__PURE__*/_jsx(Box, {
                    component: "span",
                    onClick: e => editing && setPresetColorPicker({
                      presetId: preset.id,
                      field: "textColor",
                      anchor: e.currentTarget
                    }),
                    sx: {
                      ...chipSx(!!preset.textColor),
                      bgcolor: preset.textColor || chipSx(!!preset.textColor).bgcolor
                    },
                    children: /*#__PURE__*/_jsx(FormatColorTextIcon, {
                      sx: {
                        fontSize: 16,
                        color: chipIconColor(preset.textColor)
                      }
                    })
                  })
                }), (editing || preset.highlight) && /*#__PURE__*/_jsx(Tooltip, {
                  title: preset.highlight ? `Highlight: ${preset.highlight}` : "Choose highlight",
                  children: /*#__PURE__*/_jsx(Box, {
                    component: "span",
                    onClick: e => editing && setPresetColorPicker({
                      presetId: preset.id,
                      field: "highlight",
                      anchor: e.currentTarget
                    }),
                    sx: {
                      ...chipSx(!!preset.highlight),
                      bgcolor: preset.highlight || chipSx(!!preset.highlight).bgcolor
                    },
                    children: /*#__PURE__*/_jsx(BorderColorIcon, {
                      sx: {
                        fontSize: 16,
                        color: chipIconColor(preset.highlight)
                      }
                    })
                  })
                }), /*#__PURE__*/_jsx(Box, {
                  sx: {
                    flex: 1
                  }
                }), editing ? /*#__PURE__*/_jsx(Button, {
                  size: "small",
                  variant: "contained",
                  onClick: () => {
                    stopEditing(preset.id);
                    setConfirmDeleteId(null);
                  },
                  startIcon: /*#__PURE__*/_jsx(CheckIcon, {
                    sx: {
                      fontSize: 14
                    }
                  }),
                  sx: {
                    ml: "auto",
                    py: 0.25,
                    px: 1,
                    fontSize: 11,
                    minWidth: 0
                  },
                  children: "Save"
                }) : /*#__PURE__*/_jsx(Tooltip, {
                  title: selTip("Apply preset to selection", presetShortcut),
                  children: /*#__PURE__*/_jsx("span", {
                    children: /*#__PURE__*/_jsx(Button, {
                      size: "small",
                      variant: "contained",
                      onClick: () => applyPreset(preset),
                      sx: {
                        ml: "auto",
                        py: 0.25,
                        px: 1,
                        fontSize: 11,
                        minWidth: 0
                      },
                      children: "Apply"
                    })
                  })
                })]
              })]
            }, preset.id);
          })
        })]
      })]
    }), presetColorPicker && /*#__PURE__*/_jsx(ColorPickerPopover, {
      anchorEl: presetColorPicker.anchor,
      open: Boolean(presetColorPicker),
      onClose: () => setPresetColorPicker(null),
      onSelect: c => {
        if (!presetColorPicker) return;
        updatePreset(presetColorPicker.presetId, {
          [presetColorPicker.field]: c ?? undefined
        });
        setPresetColorPicker(null);
      },
      currentColor: formatPresets.find(p => p.id === presetColorPicker.presetId)?.[presetColorPicker.field],
      title: presetColorPicker.field === "textColor" ? "Preset text color" : "Preset highlight",
      removeLabel: "None"
    }), !hideAssetsControls && false && /*#__PURE__*/_jsx(Box, {
      sx: {
        px: 2,
        pb: 1,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        flex: 1,
        overflowY: "auto",
        minHeight: 0
      },
      children: Array.isArray(assets) && assets.map(a => /*#__PURE__*/_jsxs(Box, {
        sx: {
          p: 1,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          opacity: a.inserted ? 0.6 : 1,
          position: "relative",
          cursor: a.inserted ? "not-allowed" : "pointer"
        },
        onClick: () => !a.inserted && toggleSelect(a.id),
        children: [/*#__PURE__*/_jsxs(Box, {
          sx: {
            position: "relative"
          },
          children: [/*#__PURE__*/_jsx(Box, {
            component: "img",
            src: a.url,
            alt: a.name,
            sx: {
              width: "100%",
              height: "auto",
              borderRadius: 0.5,
              mb: 1,
              display: "block"
            }
          }), !a.inserted && selected[a.id] && /*#__PURE__*/_jsx(Box, {
            sx: {
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(255,255,255,0.4)",
              borderRadius: 0.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              fontWeight: 700,
              color: "success.main"
            },
            children: "\u2713"
          })]
        }), /*#__PURE__*/_jsxs(Box, {
          sx: {
            display: "flex",
            gap: 1
          },
          children: [/*#__PURE__*/_jsxs(TextField, {
            size: "small",
            select: true,
            label: "Size",
            value: a.size || "medium",
            onChange: e => onUpdateAssetSize?.(a.id, e.target.value),
            sx: {
              minWidth: 100
            },
            children: [/*#__PURE__*/_jsx(MenuItem, {
              value: "small",
              children: "Small"
            }), /*#__PURE__*/_jsx(MenuItem, {
              value: "medium",
              children: "Medium"
            }), /*#__PURE__*/_jsx(MenuItem, {
              value: "large",
              children: "Large"
            })]
          }), !a.inserted ? /*#__PURE__*/_jsx(Button, {
            size: "small",
            variant: "outlined",
            onClick: () => onInsertAssetsAsRow?.([a.id]),
            children: "Insert"
          }) : /*#__PURE__*/_jsx(Button, {
            size: "small",
            variant: "text",
            color: "error",
            onClick: () => onRemoveAsset?.(a.id),
            children: "Remove"
          })]
        })]
      }, a.id))
    }), /*#__PURE__*/_jsx(Divider, {}), /*#__PURE__*/_jsxs(Box, {
      sx: {
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        flexShrink: 0
      },
      children: [/*#__PURE__*/_jsx(Button, {
        variant: "contained",
        size: "small",
        startIcon: previewMode ? /*#__PURE__*/_jsx(EditIcon, {}) : /*#__PURE__*/_jsx(PreviewIcon, {})
        // Prevent the contenteditable from losing focus on mouse
        // down. Otherwise the editor's blur handler fires a state
        // update between mousedown and click, the toolbar
        // re-renders, and the synthetic click is dropped — forcing
        // the user to click a second time to actually toggle.
        ,
        onMouseDown: e => e.preventDefault(),
        onClick: onTogglePreview,
        sx: {
          ...(accentColor && {
            bgcolor: accentColor,
            "&:hover": {
              bgcolor: accentColor
            }
          })
        },
        children: previewMode ? "Edit Mode" : "Preview Submission"
      }), bottomExtras]
    })]
  });
}
{
  /* Scrollable assets list */
}